import classNames from "classnames";
import React, { useEffect, useRef, useState } from "react";
import Slider from "react-slick";
import { Progress } from "../Progress";
import styles from "./Stack.scss";
import { Uploading } from "../Uploading/Uploading";
import { FormattedMessage } from "react-intl";

const msgKey = "stack";

// One card per scroll: a trackpad sends many wheel events per gesture
const WHEEL_THROTTLE_MS = 400;

export const Stack = ({
    children,
    locked,
    currentIndex,
    onChange,
    onChanged,
    uploading,
    tools,
    badge,
    user,
    hideProgress
}) => {
    const sliderRef = useRef(null);
    const lastWheelRef = useRef(0);
    const [numberOfCards, setNumberOfCards] = useState(null);

    useEffect(() => {
        setNumberOfCards(React.Children.toArray(children)?.length || 0);
    }, [children]);

    useEffect(() => {
        sliderRef?.current?.slickGoTo(currentIndex);
    }, [currentIndex]);

    const handleWheel = (e) => {
        if (locked) return;
        const now = Date.now();
        if (now - lastWheelRef.current < WHEEL_THROTTLE_MS) return;
        lastWheelRef.current = now;
        if (e.deltaY > 0) {
            sliderRef?.current?.slickNext();
        } else {
            sliderRef?.current?.slickPrev();
        }
    };

    return (
        <div
            className={classNames(styles.base, {
                [styles.uploading]: uploading !== null
            })}
            onWheel={handleWheel}
        >
            <div className={styles.wrapper}>
                <div className={styles.cards}>
                    {/* Error */}
                    <div className={styles.error}>
                        <FormattedMessage id={`${msgKey}.screenTooSmall`} />
                    </div>

                    {/*  Uploading */}
                    {uploading !== null && (
                        <div className={styles.uploading}>
                            <Uploading percent={uploading} />
                        </div>
                    )}

                    {/* Controls */}
                    <div className={classNames(styles.controls, { [styles.withUser]: !!user })}>
                        <div className={styles.leftSide}>
                            <Progress hidden={hideProgress} current={currentIndex + 1} total={numberOfCards} />
                            {badge && <div className={styles.badge}>{badge}</div>}
                        </div>
                        <div className={styles.rightSide}>
                            {!!user && <div className={styles.user}>{user}</div>}
                            <div className={styles.tools}>{tools}</div>
                        </div>
                    </div>
                    <Slider.default
                        ref={sliderRef}
                        className={styles.slider}
                        vertical
                        verticalSwiping
                        adaptiveHeight
                        swipeToSlide
                        infinite={false}
                        arrows={false}
                        speed={200}
                        // A change during the slide animation (e.g. tapping Next fast) would otherwise be ignored,
                        // leaving the slider on a card that isn't the current one: empty, with no Next button
                        waitForAnimate={false}
                        slidesToShow={1}
                        slidesToScroll={1}
                        buttons={false}
                        beforeChange={(_, nextIndex) => onChange(nextIndex)}
                        afterChange={onChanged}
                        swipe={!locked}
                    >
                        {React.Children.map(children, (child, index) => {
                            return child ? React.cloneElement(child, { index }) : null;
                        })}
                    </Slider.default>
                </div>
            </div>
        </div>
    );
};
